import { finalizeControlCard, previewControlCard } from '@/features/control-card'
import type { GenerateControlCardRequest } from '@/features/control-card/application/dtos/ControlCardDTOs'
import { STM_LOGO, CERT_LOGO } from '@/assets/control card'
import { supabase } from '@/lib/supabase/client'
import { uploadDocument } from '@/services/document-upload.service'
import { downloadJobImages } from '@/services/jobImages.service'
import {
  addYearsToIsoDate,
  buildEtReport,
  fetchEtCompany,
  profileHasEt,
  renewNdtDates,
  saveEtReport,
  type BuildEtReportInput,
  type EtCompany,
} from '@/services/month48Inspection.service'
import {
  STMIDBEquipmentService,
  getSTMIDBUploadCode,
  mapWLLDocumentTypeToSTMIDB,
} from '@/services/api/stmidbEquipmentService'
import {
  BULK_CONTROL_CARD_WORK_TYPES,
  CONTROL_CARD_STATUSES,
  markJobAssignmentCardPublished,
  updateEquipmentInspectionStatus,
  type InspectionStatus,
  type Job,
  type JobAssignment,
  type JobEquipment,
} from '@/services/jobs.service'
import { mergePdfBlobs } from '@/utils/mergePdfBlobs'

export interface PendingControlCard {
  job: Job
  assignment: JobAssignment
  equipment: JobEquipment
  status: InspectionStatus
}

export interface ControlCardSigner {
  full_name: string
  signature: string | null
  /** ET-qualified inspectors may publish 48-month inspections. */
  has_et: boolean
}

export interface ControlCardSettings {
  performedDate: string
  controlledAgainst: string
  performedBy: ControlCardSigner
}

export interface PublishJobControlCardsInput extends ControlCardSettings {
  cards: PendingControlCard[]
  onProgress?: (done: number, total: number) => void
}

/** 48-month work passed as OK: the NDT was done, so it gets an ET report and renewed NDT dates. */
export const isMonth48WithNdt = (card: Pick<PendingControlCard, 'assignment' | 'status'>) =>
  card.assignment.work_type === '48_months' && card.status === 'OK'

/** Own assignments with a picked status that still need their control card. */
export const pendingControlCards = (jobs: Job[], inspectorId: string | null): PendingControlCard[] =>
  jobs
    .filter((job) => job.inspector_id === inspectorId)
    .flatMap((job) =>
      job.assignments.flatMap((assignment) =>
        assignment.equipment &&
        assignment.inspection_status &&
        CONTROL_CARD_STATUSES.includes(assignment.inspection_status) &&
        BULK_CONTROL_CARD_WORK_TYPES.includes(assignment.work_type) &&
        !assignment.control_card_at
          ? [{ job, assignment, equipment: assignment.equipment, status: assignment.inspection_status }]
          : [],
      ),
    )

/** The signed-in user as "utført av" on the cards. */
export async function fetchControlCardSigner(): Promise<ControlCardSigner | null> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null
  const { data: profile } = await supabase
    .from('profiles')
    .select('full_name, signature, et, et_level')
    .eq('id', user.id)
    .maybeSingle()
  return profile?.full_name?.trim()
    ? { full_name: profile.full_name.trim(), signature: profile.signature, has_et: profileHasEt(profile) }
    : null
}

const remarksOf = (card: PendingControlCard) => card.assignment.inspection_remarks?.trim() || null

const responsibleOf = (card: PendingControlCard) => {
  if (!card.job.creator) throw new Error('Jobben mangler ordreansvarlig (opprettet av)')
  return card.job.creator
}

const cardRequest = async (card: PendingControlCard, settings: ControlCardSettings): Promise<GenerateControlCardRequest> => {
  const responsible = responsibleOf(card)
  const withNdt = isMonth48WithNdt(card)
  const attachmentImages = await downloadJobImages(card.assignment.inspection_images)
  return {
    equipmentId: card.equipment.id,
    inspection: {
      jobType: withNdt ? '48 måneder' : 'Periodisk kontroll',
      performedDate: settings.performedDate,
      performedBy: settings.performedBy.full_name,
      responsiblePerson: responsible.full_name,
      controlledAgainst: settings.controlledAgainst,
      status: card.status,
      remarks: remarksOf(card) ?? undefined,
      orderNo: card.job.order_number?.trim() || undefined,
      performedBySignature: settings.performedBy.signature ?? undefined,
      responsiblePersonSignature: responsible.signature ?? undefined,
    },
    logos: { company: STM_LOGO, certification: CERT_LOGO },
    options: { attachmentImages, ...(withNdt && { ndtDateOverride: settings.performedDate }) },
  }
}

const etReportInput = (card: PendingControlCard, settings: ControlCardSettings, company: EtCompany): BuildEtReportInput => ({
  equipment: { ...card.equipment, categoryCode: card.equipment.category_code },
  isSTMIDB: card.equipment.source === 'stmidb',
  company,
  orderNo: card.job.order_number,
  performedDate: settings.performedDate,
  status: card.status,
  remarks: remarksOf(card),
  issuedBy: settings.performedBy.full_name,
  verifiedBy: responsibleOf(card).full_name,
})

const requireEtCompany = async (card: PendingControlCard) => {
  const company = await fetchEtCompany(card.equipment.id, card.equipment.source === 'stmidb')
  if (!company) throw new Error('Fant ikke kunden for ET-rapporten')
  return company
}

async function publishCard(card: PendingControlCard, settings: ControlCardSettings) {
  const { equipment, assignment, job, status } = card
  const withNdt = isMonth48WithNdt(card)
  if (withNdt && !settings.performedBy.has_et) {
    throw new Error('48-måneders kontroll krever ET-kvalifikasjon for utførende inspektør')
  }
  const isSTMIDB = equipment.source === 'stmidb'
  const etCompany = withNdt ? await requireEtCompany(card) : null
  const request = await cardRequest(card, settings)

  if (withNdt) await renewNdtDates(equipment.id, settings.performedDate, isSTMIDB)

  const result = await finalizeControlCard(request)
  if (!result.success || !result.blob) throw new Error(result.error || 'Kunne ikke generere kontrollkort')

  const serialNumber = equipment.serial_number || equipment.asset_number || 'unknown'
  const reportNumber = result.reportNumber || `STMI-${settings.performedDate}`
  const file = new File([result.blob], `${reportNumber}.pdf`, { type: 'application/pdf' })

  if (isSTMIDB) {
    await STMIDBEquipmentService.uploadDocument({
      equipmentId: equipment.id,
      equipmentCode: getSTMIDBUploadCode(equipment),
      documentType: mapWLLDocumentTypeToSTMIDB('control_card'),
      file,
    })
  } else {
    const upload = await uploadDocument({
      file,
      customerName: equipment.company_name,
      serialNumber,
      equipmentId: equipment.id,
      documentType: 'control_card',
      inspectionStatus: status,
    })
    if (!upload.success) throw new Error(upload.error || 'Opplasting feilet')
  }

  await updateEquipmentInspectionStatus(equipment, status, {
    last_inspection_date: settings.performedDate,
    next_inspection_date: addYearsToIsoDate(settings.performedDate, 1),
    ...(job.order_number?.trim() && { order_no: job.order_number.trim() }),
  })

  if (etCompany) {
    await saveEtReport({ ...etReportInput(card, settings, etCompany), approvedById: job.creator?.id ?? null })
  }

  await markJobAssignmentCardPublished(assignment.id, status, remarksOf(card))
}

/** Publishes one control card per pending result; returns the cards that failed. */
export async function publishJobControlCards(input: PublishJobControlCardsInput) {
  const failed: { card: PendingControlCard; error: string }[] = []
  for (let i = 0; i < input.cards.length; i++) {
    input.onProgress?.(i, input.cards.length)
    try {
      await publishCard(input.cards[i], input)
    } catch (err) {
      console.error('Control card publishing failed:', err)
      failed.push({ card: input.cards[i], error: err instanceof Error ? err.message : 'Ukjent feil' })
    }
  }
  input.onProgress?.(input.cards.length, input.cards.length)
  return { published: input.cards.length - failed.length, failed }
}

/**
 * Watermarked preview of every pending card in one PDF, each 48-month card
 * followed by its ET report. Nothing is saved.
 */
export async function previewJobControlCards(input: PublishJobControlCardsInput) {
  const blobs: Blob[] = []
  const failed: { card: PendingControlCard; error: string }[] = []
  for (let i = 0; i < input.cards.length; i++) {
    const card = input.cards[i]
    input.onProgress?.(i, input.cards.length)
    try {
      const result = await previewControlCard(await cardRequest(card, input))
      if (!result.success || !result.blob) throw new Error(result.error || 'Kunne ikke generere kontrollkort')
      blobs.push(result.blob)
      if (isMonth48WithNdt(card)) {
        const { pdfBlob } = await buildEtReport(etReportInput(card, input, await requireEtCompany(card)))
        blobs.push(pdfBlob)
      }
    } catch (err) {
      console.error('Control card preview failed:', err)
      failed.push({ card, error: err instanceof Error ? err.message : 'Ukjent feil' })
    }
  }
  input.onProgress?.(input.cards.length, input.cards.length)
  return { blob: blobs.length > 0 ? await mergePdfBlobs(blobs) : null, failed }
}
